import { useAuth } from "wasp/client/auth";
import {
  PaymentPlanId,
  SubscriptionStatus,
  graceDaysRemaining,
  gracePeriodEndsAt,
  isGracePeriodExpired,
  prettyPaymentPlanName,
} from "./plans";

export interface PaymentGraceState {
  /** Grace en cours : paiement echoue, acces encore complet. */
  isInGrace: boolean;
  /** Grace ecoulee ET retrogradation constatee : l'acces est deja reduit. */
  isExpired: boolean;
  /** Jours entiers restants (0 si expiree). */
  daysRemaining: number;
  /** Fin de grace, pour l'affichage de la date butoir. */
  endsAt: Date | null;
  /** Nom lisible du plan concerne, ou null. */
  planName: string | null;
}

const NO_GRACE: PaymentGraceState = {
  isInGrace: false,
  isExpired: false,
  daysRemaining: 0,
  endsAt: null,
  planName: null,
};

/** Etat de la periode de grace pour l'utilisateur courant.
 *
 *  Lit `useAuth` plutot qu'une query dediee : Wasp expose deja l'entite User
 *  complete au client, et le champ est rafraichi a chaque invalidation d'auth.
 *  Une query de plus ferait un aller-retour reseau pour une donnee deja la.
 *
 *  Les memes helpers que le serveur sont utilises : une divergence entre la
 *  regle affichee et la regle appliquee serait pire que pas d'affichage du tout. */
export function usePaymentGrace(): PaymentGraceState {
  const { data: user } = useAuth();

  if (!user?.paymentFailedAt) return NO_GRACE;

  const failedAt = user.paymentFailedAt;
  const planName = user.subscriptionPlan
    ? prettyPaymentPlanName(user.subscriptionPlan as PaymentPlanId)
    : null;

  // L'acces est deja coupe des que l'UNE de ces conditions tient :
  //  - la grace a expire (regle Edgely, appliquee par getUserPlan) ;
  //  - Stripe a annule l'abonnement (statut `deleted`) : il coupe au bout de ses
  //    propres 7 jours de relances, donc potentiellement avant l'instant exact
  //    d'expiration cote Edgely. Sans ce test, l'utilisateur lirait « il vous
  //    reste 1 jour » alors qu'il est deja retombe en Free ;
  //  - le plan est deja repasse a Free (job de retrogradation passe).
  // Le mensonge inverse (annoncer la fin alors que l'acces tient) est preferable
  // a promettre du temps qui n'existe plus.
  const accessAlreadyLost =
    isGracePeriodExpired(failedAt) ||
    user.subscriptionStatus === SubscriptionStatus.Deleted ||
    user.subscriptionPlan === PaymentPlanId.Free;

  return {
    isInGrace: !accessAlreadyLost,
    isExpired: accessAlreadyLost,
    daysRemaining: graceDaysRemaining(failedAt),
    endsAt: gracePeriodEndsAt(failedAt),
    planName,
  };
}
